import styled from '@emotion/styled';
import React, { useEffect, useMemo } from 'react';
import { RelayType } from '@diritto/common/content/constants';
import { TrackerExtraInfo, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { EmptyList } from 'ui-components/common/EmptyList';
import { InfiniteScrollWrapper } from 'ui-components/common/InfiniteScrollWrapper';
import { ContentBodyContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { FancyShareItem } from 'app-components/fancyShare/FancyShareItem';
import { Loading } from 'ui-components/common/Loading';
import { useFancyShareDispatchState, useFancyShareState } from 'contexts/FancyShareProvider';
import { DirittoMessageInput } from 'app-components/common/DirittoMessageInput';
import { FancyShareAction } from 'constants/fancyShare';
import { DesktopContentWidth, isPC } from 'constants/layout';
import { ZIndex } from 'constants/zIndex';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useBlockProfiles } from 'state-hooks/accounts/useBlockProfiles';

interface FancyShareListProps {
  trackerExtraInfo: TrackerExtraInfo;
  emptyDescription?: string;
}

export const FancyShareList = ({
  trackerExtraInfo,
  emptyDescription = '남겨진 이멋공이 없어요',
}: FancyShareListProps) => {
  const { isPending, hasNextPage, fetchNextPage, fancyShares, likeFancyShareIds, profiles, contents, episodes } =
    useFancyShareState();
  const blockedProfileIds = useBlockProfiles();

  const filteredFancyShares = useMemo(
    () => fancyShares.filter(fancyShare => !blockedProfileIds.has(fancyShare.profileId)),
    [fancyShares, blockedProfileIds],
  );

  const {
    dispatchAction,
    fancyShare: editFancyShare,
    isPending: isActionLoading,
    isInputFocus,
  } = useFancyShareDispatchState();

  const { tracker } = useTrackingState();
  useEffect(() => {
    tracker.track(TrackingEventType.ViewFancyShareList, { trackerExtraInfo });
  }, [tracker, trackerExtraInfo]);

  return (
    <>
      {isInputFocus && (
        <StyledDirittoMessageInput
          initialValue={editFancyShare?.description}
          isLoading={isActionLoading}
          onCancelWrite={() => dispatchAction({ action: FancyShareAction.CancelEdit })}
          onRegister={(description: string) =>
            editFancyShare &&
            dispatchAction({ action: FancyShareAction.Update, fancyShare: editFancyShare, data: { description } })
          }
          maxLength={1000}
          placeholder="이 문장을 공유하고 싶은 이유를 글로 적어보세요"
        />
      )}
      <InfiniteScrollWrapper hasNext={hasNextPage} loadMore={fetchNextPage}>
        <ContentBodyContainer>
          {!isPending && filteredFancyShares.length === 0 && <EmptyList description={emptyDescription} />}
          <StyledList>
            {filteredFancyShares.map(fancyShare => {
              const profile = profiles[fancyShare.profileId];
              const content = contents[fancyShare.contentId];
              const episode = episodes[fancyShare.episodeId];
              const authorProfile =
                content && content.relayType === RelayType.Public && content.profileId
                  ? profiles[content.profileId]
                  : undefined;

              return (
                <FancyShareItem
                  key={fancyShare.id}
                  fancyShare={fancyShare}
                  isLiked={likeFancyShareIds.includes(fancyShare.id)}
                  profile={profile}
                  authorProfile={authorProfile}
                  episode={episode?.relayType === RelayType.Public ? episode : undefined}
                  content={content?.relayType === RelayType.Public ? content : undefined}
                  onDispatchAction={dispatchAction}
                  trackerExtraInfo={trackerExtraInfo}
                  onClick={() => tracker.track(TrackingEventType.ViewFancyShareList, { trackerExtraInfo })}
                />
              );
            })}
            {isPending && <Loading />}
          </StyledList>
        </ContentBodyContainer>
      </InfiniteScrollWrapper>
    </>
  );
};

export const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 100px;
  gap: 16px;
  min-height: 300px;
`;

const StyledDirittoMessageInput = styled(DirittoMessageInput)`
  position: fixed;
  bottom: 0;
  z-index: ${ZIndex.FancyShareInput};

  @media (${isPC}) {
    width: ${DesktopContentWidth - 2}px;
  }
`;
